/**
 * Format date string to display format
 * @param dateStr Date string in ISO format
 * @returns Formatted date string or empty string if dateStr is null/undefined
 */
export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  
  // Parse the date string
  const date = new Date(dateStr);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  // Format as YYYY/MM/DD
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).replace(/\//g, '/');
};

/**
 * Format date and time string to display format
 * @param dateTimeStr Date and time string in ISO format
 * @returns Formatted date and time string or empty string if dateTimeStr is null/undefined
 */
export const formatDateTime = (dateTimeStr: string | null | undefined): string => {
  if (!dateTimeStr) return '';
  
  // Parse the date string
  const date = new Date(dateTimeStr);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  // Format as YYYY/MM/DD HH:MM
  return `${formatDate(dateTimeStr)} ${date.toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })}`;
};

const formatWithJapaneseLocale = (
  value?: string | null,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!value) return '';

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('ja-JP', options);
  }

  return '';
};

/**
 * Format date string like "2025年3月10日(月)" to match event detail view.
 */
export const formatJapaneseDateWithWeekday = (
  value?: string | null
): string => {
  const formatted = formatWithJapaneseLocale(value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short'
  });

  if (formatted) {
    return formatted;
  }

  if (!value) return '';

  // fallback for YYYY-MM-DD strings that failed to parse
  return value.replace(/(\d+)-0?(\d+)-0?(\d+)T?.*/, '$1年$2月$3日');
};

const TIME_PATTERN = /(\d{1,2}):(\d{2})/;

/**
 * Format time strings such as "18:30" or ISO date strings into "HH:MM".
 */
export const formatJapaneseTime = (value?: string | null): string => {
  if (!value) return '';
  const trimmed = value.trim();
  const match = trimmed.match(TIME_PATTERN);
  if (match) {
    const hour = match[1].padStart(2, '0');
    const minute = match[2].padStart(2, '0');
    return `${hour}:${minute}`;
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return `${padTwoDigits(parsed.getHours())}:${padTwoDigits(parsed.getMinutes())}`;
  }

  return '';
};

const padTwoDigits = (value: number): string => String(value).padStart(2, '0');

const normalizeTimeString = (
  timeStr: string | null | undefined,
  fallbackDate: Date
): string => {
  if (timeStr) {
    const segments = timeStr.split(':');
    const hour = segments[0] ?? '';
    const minute = segments[1] ?? '00';

    if (hour !== '' && !Number.isNaN(Number(hour))) {
      return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
    }
  }

  return `${padTwoDigits(fallbackDate.getHours())}:${padTwoDigits(
    fallbackDate.getMinutes()
  )}`;
};

/**
 * Format separate date and time fields into "YYYY年M月D日 HH:MM"
 */
export const formatJapaneseDateTime = (
  dateStr: string | null | undefined,
  timeStr?: string | null
): string => {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const time = normalizeTimeString(timeStr, date);

  return `${year}年${month}月${day}日 ${time}`.trim();
};

/**
 * Calculate how many days are left until the specified date
 * @param dateStr Date string in ISO format
 * @returns Number of days left or null if dateStr is invalid
 */
export const getDaysLeft = (dateStr: string | null | undefined): number | null => {
  if (!dateStr) return null;
  
  const targetDate = new Date(dateStr);
  if (isNaN(targetDate.getTime())) return null;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};
